import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

// ── Tipler ─────────────────────────────────────────────────────────────────

/** Araç satırının günün şoförünü hangi katmanın verdiği (sefer > görevlendirme > zimmet). */
export type DaySource = 'trip' | 'assignment' | 'default';

export interface DayVehicleRow {
  vehicleId: string;
  plate: string;
  driverId: string | null;
  source: DaySource | null;
  /** Sefer katmanı ile görevlendirme çelişiyor. */
  conflict: boolean;
  /** Satırın hangi günden geldiği (yalnız görevlendirmede; bugün+yarın karışık olabilir). */
  effectiveDay?: string | null;
}

/** Görevlendirme satırı (etkin satırlar). `vehicleId: null` = yedek. */
export interface DayAssignmentRow {
  day: string;
  driverId: string;
  vehicleId: string | null;
  isBackup: boolean;
}

export interface DayRosterResponse {
  day: string;
  rosterDay: string;
  readOnly: boolean;
  vehicles: DayVehicleRow[];
  assignments: DayAssignmentRow[];
  backups: { driverId: string }[];
  /** Akşam geçişinde yarın satırıyla aracını kaybeden şoförler. */
  lostDrivers: { driverId: string; day: string }[];
  rematchFailed?: boolean;
}

/** PUT/DELETE yanıtı: yazım sonrası yeniden eşleme düştüyse `rematchFailed` true. */
export interface DayWriteResult {
  rematchFailed?: boolean;
}

// ── Sorgu ───────────────────────────────────────────────────────────────────

export function useDayAssignments(day: string | null) {
  return useQuery({
    queryKey: ['day-assignments', day],
    queryFn: () => api.get<DayRosterResponse>(`/api/day-assignments?day=${day}`),
    enabled: day !== null,
  });
}

// ── Yazma ───────────────────────────────────────────────────────────────────

function useInvalidateDayAssignments() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['day-assignments'] });
}

/** `PUT /api/day-assignments` — gün+şoför tekil; `vehicleId: null` yedek yapar. */
export function useSetDayAssignment() {
  const invalidate = useInvalidateDayAssignments();
  return useMutation({
    mutationFn: (v: { day: string; driverId: string; vehicleId: string | null }) =>
      api.put<DayWriteResult>('/api/day-assignments', v),
    onSuccess: () => invalidate(),
  });
}

/** `DELETE /api/day-assignments?day=&driverId=`. */
export function useClearDayAssignment() {
  const invalidate = useInvalidateDayAssignments();
  return useMutation({
    mutationFn: (v: { day: string; driverId: string }) =>
      api.delete<DayWriteResult>(
        `/api/day-assignments?day=${v.day}&driverId=${encodeURIComponent(v.driverId)}`,
      ),
    onSuccess: () => invalidate(),
  });
}
